import { format, parseISO, differenceInMinutes } from "date-fns";
import { Plane, Building2, CalendarPlus, CarTaxiFront } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { TripElement, FlightDetails } from "@/types/trip";
import Hey from "@/lib/hey-utils";
import { useTranslation } from "react-i18next";
import { trackTimelineAction, trackTripCardAction } from "@/utils/analytics";
import { CarRentalElement } from "./CarRentalElement";

interface TripCardElementProps {
  element: TripElement;
  getAirportInfo: (flightElement: any, type: 'departure' | 'arrival') => Hey.AirportInformations;
  handleCheckFlightStatus?: (flightNumber: string) => void;
  tripId: string;
  destinationName: string;
}

const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}`;
};

export const TripCardElement = ({ element, getAirportInfo, handleCheckFlightStatus, tripId, destinationName }: TripCardElementProps) => {
  const { t } = useTranslation();
  
  const calculateFlightDuration = (departureTime: string, arrivalTime: string): string => {
    const departureDate = parseISO(departureTime || "1970");
    const arrivalDate = parseISO(arrivalTime || "1970");
    const durationInMinutes = differenceInMinutes(arrivalDate, departureDate);
    return formatDuration(durationInMinutes);
  };

  const isAppleDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    console.debug("TripCardElement - User agent:", userAgent);
    const isApple = /iphone|ipad|ipod|mac|macintosh/.test(userAgent);
    console.debug("TripCardElement - Is Apple device:", isApple);
    return isApple;
  };

  const addToCalendar = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling up
    
    if (element.type === 'flight') {
      const flightDetails = element.details as FlightDetails;
      const startDate = parseISO(flightDetails.departure.time || "1970");
      const endDate = parseISO(flightDetails.arrival.time || "1970");
      
      const title = `Flight: ${flightDetails.airline} ${flightDetails.flightNumber}`;
      const description = `From: ${flightDetails.departure.code} (${getAirportInfo(element, 'departure').cityName})
To: ${flightDetails.arrival.code} (${getAirportInfo(element, 'arrival').cityName})
Airline: ${flightDetails.airline}
Flight Number: ${flightDetails.flightNumber}`;
      
      const deviceType = isAppleDevice() ? "Apple" : "non-Apple";
      console.debug(`TripCardElement - Flight - Detected ${deviceType} device, choosing appropriate calendar...`);
      
      // Track the calendar add action
      trackTimelineAction(
        "add_to_calendar", 
        "flight", 
        tripId, 
        destinationName
      );
      
      if (isAppleDevice()) {
        console.debug("TripCardElement - Flight - Opening Apple Calendar");
        openAppleCalendar(title, description, startDate, endDate, flightDetails.departure.code);
      } else {
        console.debug("TripCardElement - Flight - Opening Google Calendar");
        openGoogleCalendar(title, description, startDate, endDate, flightDetails.departure.code);
      }
    } 
    else if (element.type === 'hotel') {
      const hotelDetails = element.details as any;
      const startDate = parseISO(element.startDate || "1970");
      const endDate = parseISO(element.endDate || "1970");
      
      const title = `Stay at ${hotelDetails.name}`;
      const description = `Hotel: ${hotelDetails.name}
Address: ${hotelDetails.address || hotelDetails.city}
${hotelDetails.roomType ? `Room Type: ${hotelDetails.roomType}` : ''}`;
      
      const deviceType = isAppleDevice() ? "Apple" : "non-Apple";
      console.debug(`TripCardElement - Hotel - Detected ${deviceType} device, choosing appropriate calendar...`);
      
      // Track the calendar add action
      trackTimelineAction(
        "add_to_calendar", 
        "hotel", 
        tripId, 
        destinationName
      );
      
      if (isAppleDevice()) {
        console.debug("TripCardElement - Hotel - Opening Apple Calendar");
        openAppleCalendar(title, description, startDate, endDate, hotelDetails.name);
      } else {
        console.debug("TripCardElement - Hotel - Opening Google Calendar");
        openGoogleCalendar(title, description, startDate, endDate, hotelDetails.name);
      }
    }
  };

  const openAppleCalendar = (title: string, description: string, startDate: Date, endDate: Date, location: string) => {
    // Format dates for Apple Calendar URL
    const formattedStartDate = startDate.toISOString();
    const formattedEndDate = endDate.toISOString();
    
    // Create Apple Calendar URL with all parameters (webcal protocol)
    const calendarUrl = `webcal://p30-calendarws.icloud.com/ca/subscribe/1/${encodeURIComponent(title)}?${
      new URLSearchParams({
        title: encodeURIComponent(title),
        desc: encodeURIComponent(description),
        location: encodeURIComponent(location),
        dtstart: formattedStartDate,
        dtend: formattedEndDate
      }).toString()
    }`;
    
    console.debug("TripCardElement - Apple Calendar URL:", calendarUrl);
    
    // Open calendar URL
    window.location.href = calendarUrl;
  };

  const openGoogleCalendar = (title: string, description: string, startDate: Date, endDate: Date, location: string) => {
    // Format dates for calendar URL (remove special characters)
    const formattedStartDate = startDate.toISOString().replace(/-|:|\.\d+/g, '');
    const formattedEndDate = endDate.toISOString().replace(/-|:|\.\d+/g, '');
    
    // Create calendar URL with all parameters
    const calendarUrl = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${formattedStartDate}/${formattedEndDate}&details=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}&sprop=&sprop=name:`;
    
    console.debug("TripCardElement - Google Calendar URL:", calendarUrl);
    
    // Open calendar URL
    window.open(calendarUrl, '_blank');
  };

  if (element.type === 'flight') {
    const flightDetails = element.details as FlightDetails;
    return (
      <div className="bg-travel-50 rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-2 text-travel-600">
          <Plane className="w-4 h-4" />
          <span className="font-semibold text-sm">
            {flightDetails.airline} {flightDetails.flightNumber}
          </span>
        </div>
        <div className="flex justify-between items-center text-sm">
          <div>
            <div className="font-bold">{flightDetails.departure.code}</div>
            <div className="text-xs text-travel-400">
              {getAirportInfo(element, 'departure').cityName}
            </div>
            <div className="text-travel-500">
              {format(parseISO(flightDetails.departure.time || "1970"), 'HH:mm')}
            </div>
          </div>
          <div className="flex flex-col items-center">
            <span className="text-xs text-travel-500 mb-1">
              {calculateFlightDuration(
                flightDetails.departure.time,
                flightDetails.arrival.time
              )}
            </span>
            <div className="border-b border-travel-200 w-8" />
          </div>
          <div className="text-right">
            <div className="font-bold">{flightDetails.arrival.code}</div>
            <div className="text-xs text-travel-400">
              {getAirportInfo(element, 'arrival').cityName}
            </div>
            <div className="text-travel-500">
              {format(parseISO(flightDetails.arrival.time || "1970"), 'HH:mm')}
            </div>
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full mt-2 bg-white text-travel-600 border-travel-200 hover:bg-travel-50 hover:text-travel-700"
          onClick={addToCalendar}
        >
          <CalendarPlus className="mr-1 h-4 w-4" />
          {t("tripCard.addToCalendar", "Add to Calendar")}
        </Button>
      </div>
    );
  }

  if (element.type === 'hotel') {
    const hotelDetails = element.details as any;
    return (
      <div className="bg-travel-50 rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-2 text-travel-600">
          <Building2 className="w-4 h-4" />
          <span className="font-semibold text-sm">
            {hotelDetails.name}
          </span>
        </div>
        <div className="text-sm text-travel-500">
          {hotelDetails.city}
        </div>
        <div className="flex justify-between text-xs text-travel-500">
          <span>{format(parseISO(element.startDate || "1970"), 'MMM d')}</span>
          <span>→</span>
          <span>{format(parseISO(element.endDate || "1970"), 'MMM d')}</span>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full mt-2 bg-white text-travel-600 border-travel-200 hover:bg-travel-50 hover:text-travel-700"
          onClick={addToCalendar}
        >
          <CalendarPlus className="mr-1 h-4 w-4" />
          {t("tripCard.addToCalendar", "Add to Calendar")}
        </Button>
      </div>
    );
  }

  if (element.type === 'car') {
    return <CarRentalElement element={element} tripId={tripId} destinationName={destinationName} />;
  }

  return null;
};
